import React from 'react';
import { storyblokEditable, StoryblokComponent } from '@storyblok/react';
import type { BlokComponent } from '../types/BlokComponent';
import type { SbBlokData } from '@storyblok/react';
import useWindowDimensions from '../hooks/useWindowDimensions';
import { columnGradient } from '../utils/columnGradient';

interface StoryblokImage {
  id: number;
  alt: string;
  name: string;
  focus: string;
  title: string;
  filename: string;
  copyright: string;
  fieldtype: string;
}

const Page = ({
  blok,
}: BlokComponent<{
  body: SbBlokData[];
  backgroundColor?: string;
  backgroundImage?: StoryblokImage;
  hasBackgroundBars?: boolean;
  backgroundBars?: string;
}>) => {
  const { width } = useWindowDimensions();
  // Preserve original falsy check: empty string from Storyblok should fall back to default
  const backgroundColor = blok.backgroundColor ? blok.backgroundColor : '#F0F0EF';

  const getColumnCount = (): 4 | 6 | 8 | null => {
    if (!blok.backgroundBars) return null;
    if (width <= 490) return 4;
    if (blok.backgroundBars === 'eight') return 8;
    if (blok.backgroundBars === 'six') return 6;
    return null;
  };

  const columnCount = getColumnCount();

  // Responsive: bars align with the content container edges (max-w-container + padding)
  const containerWidth = Math.min(width, 1440);
  const innerPadding = width >= 1120 ? 80 : width >= 768 ? 40 : 20;
  const barsBgSize = `${containerWidth - 2 * innerPadding}px auto`;

  const bgParts: string[] = [];
  const bgSizes: string[] = [];
  const bgPositionY: string[] = [];

  if (blok.backgroundImage) {
    bgParts.push(`url(${blok.backgroundImage.filename})`);
    bgSizes.push('127%');
    bgPositionY.push('160px');
  }
  if (columnCount) {
    bgParts.push(columnGradient(columnCount));
    bgSizes.push(barsBgSize);
    bgPositionY.push('303px');
  }

  const style: React.CSSProperties = {
    backgroundColor,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    ...(bgParts.length > 0 && {
      backgroundImage: bgParts.join(', '),
      backgroundSize: bgSizes.join(', '),
      backgroundPosition: 'center center',
      backgroundPositionY: bgPositionY.join(', '),
      backgroundRepeat: 'no-repeat',
    }),
  };

  return (
    <div {...storyblokEditable(blok)} style={style}>
      {blok.body?.map((nestedBlok) => (
        <StoryblokComponent blok={nestedBlok} key={nestedBlok._uid} />
      ))}
    </div>
  );
};

export default Page;
